import { useId, useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { listConnectors, type ListedConnector } from "../../../api/connectorsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import ProviderCards from "../providers/ProviderCards";
import ConnectSteps from "../setup/ConnectSteps";
import { GUIDE_STARTS_AT_PAIRING, WAITING_POLL_MILLISECONDS, useRefreshWhile } from "../setup/useConnectorSetup";
import { pcConnectorsOf, reportedPcsOf } from "../setup/setupProgress";
import { useSession } from "../../../session/sessionContext";
import AppButton from "../../../ui/buttons/AppButton";
import { readThisPcMark, thisPcViewOf, writeThisPcMark, type ThisPcMark } from "../thisPc/thisPc";
import { usePairedHere } from "../thisPc/usePairedHere";
import { FollowPcProvider, useFollowing } from "../pcPage/followPc";
import ConnectorPcLine, { type PcLineAction } from "./ConnectorPcLine";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ButtonLink from "../../../ui/buttons/ButtonLink";
import HelpArticleLink from "../../help/HelpArticleLink";

/**
 * `/profile/connectors` (F46, F49, F78, F86; W-06): whether this PC is connected comes first.
 * With no PC yet, the steps that connect it. Otherwise this PC and its agents first, the other
 * PCs after; among several PCs that this browser wasn't marked for, it asks once which one the
 * person is at (thisPc/). "Connect another PC" comes last; what a connector does is the help.
 */
export default function ConnectorsPage() {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listConnectors(signal), []);
  const { state, reload, refresh } = useLoad(load);
  const following = useFollowing(refresh);
  // The first PC pairs from the connector app, with no page coming back here (F122); it is
  // asked again until a PC has reported its agents, as the setup waits for it. So it is while a
  // sign-in or a PC's page opened from here is open there (F161).
  useRefreshWhile((state.status === "loaded" && reportedPcsOf(state.value).length === 0) || following.value.following, WAITING_POLL_MILLISECONDS, following.tick);

  return (
    <div className="w-full max-w-3xl space-y-6">
      <div className="space-y-1">
        <h1 className="app-title text-xl font-semibold">{t("connectors.list.title")}</h1>
        <p className="app-text-muted text-sm">{t("connectors.list.intro")}</p>
      </div>
      {/* The server may well have answered: the list is what failed (TR-B5-04). */}
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} failedTitle={t("connectors.list.loadFailed")} />
      <FollowPcProvider value={following.value}>
        {state.status === "loaded" ? <ConnectorsOverview connectors={state.value} onChanged={reload} /> : null}
      </FollowPcProvider>
    </div>
  );
}

function ConnectorsOverview({ connectors, onChanged }: { connectors: readonly ListedConnector[]; onChanged: () => void }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const userId = session.status === "signedIn" ? session.session.account.userId : "";
  const [mark, setMark] = useState<ThisPcMark>(() => readThisPcMark(userId));
  const remember = useCallback((next: ThisPcMark) => { writeThisPcMark(userId, next); setMark(next); }, [userId]);
  const pcs = pcConnectorsOf(connectors);
  usePairedHere(userId, pcs, remember);
  // A "DMW server" host a key-use grant paired is not a PC: never this one, listed after the PCs.
  const hosts = connectors.filter((connector) => connector.state === "active" && connector.hostedByServer);
  const view = thisPcViewOf(pcs, mark);
  const line = (connector: ListedConnector, thisPcAction?: PcLineAction, rowAction?: PcLineAction) => (
    <ConnectorPcLine key={connector.connectorId} connector={connector} onChanged={onChanged} thisPcAction={thisPcAction} rowAction={rowAction} />
  );
  const iAmHere = (connector: ListedConnector): PcLineAction => ({ label: t("connectors.thisPc.iAmHere"), onClick: () => { remember(connector.connectorId); } });
  return (
    <>
      {view.kind !== "no_pc" ? null : (
        <>
          <section className="app-card space-y-4 p-4" aria-label={t("connectors.list.noPc")}>
            <EmptyState>{t("connectors.list.noPc")}</EmptyState>
            <ConnectSteps />
          </section>
          {/* A "DMW server" host stays visible without a PC of one's own (Sol N3). */}
          {hosts.length === 0 ? null : <PcSection title={t("connectors.list.yourPcs")}>{hosts.map((host) => line(host))}</PcSection>}
        </>
      )}
      {view.kind !== "this_pc" ? null : (
        <>
          <PcSection title={t("connectors.list.thisPc")}>
            {/* Only a PC this browser was marked for can be wrong; one paired PC is this one (F78). */}
            {line(view.pc, mark === view.pc.connectorId || pcs.length === 1 ? { label: t("connectors.thisPc.notThisPc"), onClick: () => { remember("none"); } } : undefined)}
          </PcSection>
          <ProvidersSection connectors={[view.pc]} />
          {view.others.length + hosts.length === 0 ? null : (
            <PcSection title={t("connectors.thisPc.otherPcs")}>
              {view.others.map((other) => line(other, iAmHere(other)))}
              {hosts.map((host) => line(host))}
            </PcSection>
          )}
          {view.others.length === 0 ? null : <ProvidersSection connectors={view.others} />}
        </>
      )}
      {view.kind !== "ask" ? null : (
        <>
          <PcSection title={t("connectors.thisPc.which")}>
            {view.pcs.map((pc) => line(pc, undefined, iAmHere(pc)))}
            <li>
              <AppButton type="button" variant="ghost" size="xs" onClick={() => { remember("none"); }}>{t("connectors.thisPc.noneOfThem")}</AppButton>
            </li>
            {hosts.map((host) => line(host))}
          </PcSection>
          <ProvidersSection connectors={view.pcs} />
        </>
      )}
      {view.kind !== "not_here" ? null : (
        <>
          <PcSection title={t("connectors.list.yourPcs")}>
            {view.pcs.map((pc) => line(pc, iAmHere(pc)))}
            {hosts.map((host) => line(host))}
          </PcSection>
          <ProvidersSection connectors={view.pcs} />
        </>
      )}
      {view.kind === "no_pc" ? null : (
        <ButtonLink to={appPaths.connectorSetup()} state={GUIDE_STARTS_AT_PAIRING} size="xs">
          {t("connectors.list.connectAnother")}
        </ButtonLink>
      )}
    </>
  );
}

function PcSection({ title, children }: { title: string; children: ReactNode }) {
  const headingId = useId();
  return (
    <section className="space-y-2" aria-labelledby={headingId}>
      <h2 id={headingId} className="app-label text-sm">{title}</h2>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

/** The agents and API keys of these PCs, each PC's state its own (F86). */
function ProvidersSection({ connectors }: { connectors: readonly ListedConnector[] }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="app-title text-base font-semibold">{t("connectors.providers.title")}</h2>
        <HelpArticleLink article="agents" />
      </div>
      <ProviderCards connectors={connectors} />
    </section>
  );
}
